import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import type { FundingRequestPrivateDto } from '@/api/kubik/credit'
import { getApprovedRegions } from '@/api/kubik/credit'
import { useApplyFinancingOffer } from '@/components/composed/financing/hooks/useApplyFinancingOffer'
import { openModal } from '@/components/composed/Modal/actions'
import { useAuthenticateUser } from '@/components/ui/AuthButton/hooks'
import type { IApplyingFinancingOfferRequestBody, IFinancingOfferFormValues } from '@/logic/financing/interfaces'
import { isNonNullable } from '@/typeguards/common'
import { EFinancingOfferType, EFinancingProductId } from '@/types'
import { messages } from './messages'

type TApplyMFOFinancingOfferProps = {
  body: IApplyingFinancingOfferRequestBody | IFinancingOfferFormValues
  withSuccessModal?: boolean
}

export const useApplyMFOFinancingOffer = () => {
  const dispatch = useDispatch()
  const { applyFinancingOffer, isSuccess } = useApplyFinancingOffer()
  const { isAuthenticated } = useAuthenticateUser()

  const getRegionsWhereExpressMFOIsAvailable = async () => {
    try {
      const response = await getApprovedRegions({ productId: EFinancingProductId.EXPRESS_MFO })

      return response.approvedRegions ?? []
    } catch {
      return []
    }
  }

  const applyMFOFianncingOffer = useCallback(
    async ({ body, withSuccessModal }: TApplyMFOFinancingOfferProps): Promise<FundingRequestPrivateDto[]> => {
      const { regions } = body
      const regionsWhereExpressMFOIsAvailable = await getRegionsWhereExpressMFOIsAvailable()
      const isRegionAvailable = isNonNullable(regions) && regionsWhereExpressMFOIsAvailable.includes(regions)

      if (!isRegionAvailable) {
        dispatch(
          openModal({
            options: {
              header: messages.title,
              dialogId: 'MessageModal',
            },
            contentOptions: {
              messages: [{ text: messages.notAvailable }],
            },
          }),
        )

        throw new Error('Регион недоступен')
      }

      const fundingRequests = await applyFinancingOffer({
        body: {
          ...body,
          productId: EFinancingProductId.EXPRESS_MFO,
          regionCode: regions,
          city: '',
        },
        isLost: !isAuthenticated,
        offerType: EFinancingOfferType.EXPRESS_MFO,
        withSuccessModal,
      })

      return fundingRequests
    },
    [applyFinancingOffer, dispatch, isAuthenticated],
  )

  return {
    applyMFOFianncingOffer,
    isSuccess,
  }
}
